/**
 * Extracts user-friendly error message from API response
 * @param {Error} error - The error object from axios
 * @returns {string} - User-friendly error message
 */
export const extractErrorMessage = error => {
  // Check if it's our custom error structure
  if (error.response?.data?.error) {
    const errorData = error.response.data.error;

    // Return user-friendly message if available
    if (errorData.user_message) {
      return errorData.user_message;
    }

    // Fallback to technical message
    if (errorData.message) {
      if (typeof errorData.message === 'string') {
        return errorData.message;
      }

      // Handle DRF validation errors (object format)
      if (typeof errorData.message === 'object') {
        return formatValidationErrors(errorData.message);
      }
    }
  }

  // Handle standard axios errors
  if (error.response) {
    // Check for DRF validation errors (direct format without error wrapper)
    if (
      error.response.status === 400 &&
      error.response.data &&
      typeof error.response.data === 'object'
    ) {
      const data = error.response.data;
      // Check if it's DRF validation format (object with field names as keys)
      if (!data.error && Object.keys(data).length > 0) {
        return formatValidationErrors(data);
      }
    }

    // Server responded with error status
    switch (error.response.status) {
      case 400:
        return 'Données invalides. Veuillez vérifier votre saisie.';
      case 401:
        return 'Vous devez être connecté pour effectuer cette action.';
      case 403:
        return "Vous n'avez pas les droits pour effectuer cette action.";
      case 404:
        return "L'élément demandé n'a pas été trouvé.";
      case 500:
        return 'Erreur interne du serveur. Veuillez réessayer plus tard.';
      default:
        return `Erreur ${error.response.status}: ${error.response.statusText}`;
    }
  }

  // Network or other errors
  if (error.request) {
    return 'Impossible de contacter le serveur. Vérifiez votre connexion internet.';
  }

  // Fallback
  return error.message || 'Une erreur inconnue est survenue.';
};

/**
 * Formats validation errors from DRF
 * @param {Object} validationErrors - Validation errors object
 * @returns {string} - Formatted error message
 */
const formatValidationErrors = validationErrors => {
  const errors = [];

  for (const [field, fieldErrors] of Object.entries(validationErrors)) {
    if (Array.isArray(fieldErrors)) {
      const fieldName = getFieldDisplayName(field);
      fieldErrors.forEach(error => {
        errors.push(`${fieldName}: ${error}`);
      });
    } else if (typeof fieldErrors === 'string') {
      const fieldName = getFieldDisplayName(field);
      errors.push(`${fieldName}: ${fieldErrors}`);
    }
  }

  return errors.join('\n') || 'Erreur de validation';
};

/**
 * Maps field names to user-friendly display names
 * @param {string} fieldName - API field name
 * @returns {string} - Display name
 */
const getFieldDisplayName = fieldName => {
  const fieldMap = {
    client: 'Client',
    carrier: 'Transporteur',
    client_price: 'Prix client',
    carrier_price: 'Prix transporteur',
    pickup_date: "Date d'enlèvement",
    delivery_date: 'Date de livraison',
    pickup_name: 'Nom enlèvement',
    delivery_name: 'Nom livraison',
    pickup_address1: 'Adresse enlèvement',
    pickup_address2: 'Adresse 2 enlèvement',
    delivery_address1: 'Adresse livraison',
    delivery_address2: 'Adresse 2 livraison',
    pickup_postcode: 'Code postal enlèvement',
    delivery_postcode: 'Code postal livraison',
    pickup_city: 'Ville enlèvement',
    delivery_city: 'Ville livraison',
    pickup_contact: 'Contact enlèvement',
    delivery_contact: 'Contact livraison',
    pickup_reference: 'Référence enlèvement',
    delivery_reference: 'Référence livraison',
    shipment_reference: 'Référence commande',
    number_of_pallets: 'Nombre de palettes',
    weight: 'Poids',
    pallet_type: 'Type de palette',
    pallet_height: 'Hauteur palette',
    footage: 'Métrage',
    comments: 'Commentaires',
    internal_comments: 'Commentaires internes',
    carrier_email: 'Email transporteur',
    chartering_datetime: "Date d'affrètement",
    entry_date: "Date d'entrée",
    shipment_stage: 'Statut dossier',
    invoicing_status: 'Statut facturation',
    non_field_errors: 'Erreur générale',
  };

  return fieldMap[fieldName] || fieldName;
};

/**
 * Gets error type from API response
 * @param {Error} error - The error object
 * @returns {string} - Error type
 */
export const getErrorType = error => {
  return error.response?.data?.error?.type || 'unknown_error';
};

/**
 * Extracts technical error details from API response
 * @param {Error} error - The error object from axios
 * @returns {string|null} - Technical error details or null
 */
export const extractTechnicalDetails = error => {
  if (error.response?.data?.error) {
    const errorData = error.response.data.error;

    if (errorData.type === 'api_error' && errorData.message) {
      if (typeof errorData.message === 'object') {
        return formatValidationErrors(errorData.message);
      }
      if (typeof errorData.message === 'string') {
        return errorData.message;
      }
    }
  }

  return null;
};

/**
 * Checks if error is a specific business error
 * @param {Error} error - The error object
 * @param {string} errorCode - The error code to check
 * @returns {boolean}
 */
export const isBusinessError = (error, errorCode) => {
  const errorData = error.response?.data?.error;
  return errorData?.type === 'business_error' && errorData?.code === errorCode;
};

/**
 * Checks if error is a validation error
 * @param {Error} error - The error object
 * @returns {boolean}
 */
export const isValidationError = error => {
  return getErrorType(error) === 'validation_error';
};

/**
 * Checks if error is an email error
 * @param {Error} error - The error object
 * @returns {boolean}
 */
export const isEmailError = error => {
  return getErrorType(error) === 'email_error';
};

/**
 * Checks if error is a PDF generation error
 * @param {Error} error - The error object
 * @returns {boolean}
 */
export const isPdfError = error => {
  return getErrorType(error) === 'pdf_error';
};

/**
 * Creates a message box config for error display
 * @param {Error} error - The error object
 * @param {Function} onConfirm - Callback for confirm button
 * @returns {Object} - Message box configuration
 */
export const createErrorMessageBoxConfig = (error, onConfirm = null) => {
  const message = extractErrorMessage(error);
  const errorType = getErrorType(error);
  const technicalDetails = extractTechnicalDetails(error);

  return {
    message,
    type: 'ok',
    loading_button: false,
    input: false,
    onConfirm: onConfirm || (() => {}),
    onCancel: onConfirm || (() => {}),
    title: getErrorTitle(errorType),
    technicalDetails,
  };
};

/**
 * Gets appropriate title for error type
 * @param {string} errorType - The error type
 * @returns {string} - Error title
 */
const getErrorTitle = errorType => {
  const titleMap = {
    business_error: 'Erreur métier',
    validation_error: 'Erreur de validation',
    email_error: "Erreur d'envoi d'email",
    pdf_error: 'Erreur de génération PDF',
    api_error: 'Erreur API',
    server_error: 'Erreur serveur',
    unknown_error: 'Erreur',
  };

  return titleMap[errorType] || 'Erreur';
};
